/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';

import type { CompanyTree, BaseLocationData, CloudConnectServerWithDeployInfo } from '@veeam-vspc/models/web-controllers';

import { BaseAddon } from '../base-addon';
import { CloudConnectServersSupplier, CompanySupplier, LocationSupplier } from '../../suppliers/web';
import { PortalFiltersIds, PortalFiltersKeys } from './enums';
import { checkItemVisibleInSection, isErrorThatNeedToBeShown } from '../../core';
import { flatOrganizationsTree } from './utils';

import type { OrganizationsFlatMap, PortalFiltersConfig, PreparedPortalFilters } from './interfaces';
import type { RequestSuccessResponse } from '../../interfaces';
import type { AddonContext } from '../base-addon';

export class PortalFiltersAddon extends BaseAddon {

    protected readonly sitesUrl: string;
    protected readonly organizationsUrl: string;
    protected readonly locationsUrl: string;

    protected readonly siteKey: string;
    protected readonly organizationKey: string;
    protected readonly locationKey: string;

    protected readonly cloudConnectServersSupplier: CloudConnectServersSupplier;
    protected readonly companySupplier: CompanySupplier;
    protected readonly locationSupplier: LocationSupplier;

    protected readonly allLocationsItem: BaseLocationData;

    readonly allLocationId: number;

    readonly isSitesFilterVisible: boolean;
    readonly isOrganizationsFilterVisible: boolean;
    readonly isLocationsFilterVisible: boolean;

    @observable sites: CloudConnectServerWithDeployInfo[] = [];
    @observable organizations: CompanyTree[] = [];
    @observable organizationsMap: OrganizationsFlatMap | null = null;
    @observable allLocations: Record<string, BaseLocationData[]> = {};
    @observable locations: BaseLocationData[] = [];

    @observable siteFilter: string[] = [];
    @observable organizationFilter: CompanyTree | null = null;
    @observable locationFilter: number | null = null;

    constructor(addonContext: AddonContext, portalFiltersConfig: PortalFiltersConfig = {}) {
        super(addonContext);

        makeObservable(this);

        const {
            sitesUrl = '/CloudConnectServers/Get',
            organizationsUrl = '/Company/GetCompaniesTree',
            locationsUrl = '/Location/GetLocations',
            siteKey = PortalFiltersKeys.SiteKey,
            organizationKey = PortalFiltersKeys.OrganizationKey,
            locationKey = PortalFiltersKeys.LocationKey,
            allLocationId = -1,
        } = portalFiltersConfig ?? {};

        this.sitesUrl = sitesUrl;
        this.organizationsUrl = organizationsUrl;
        this.locationsUrl = locationsUrl;

        this.siteKey = siteKey;
        this.organizationKey = organizationKey;
        this.locationKey = locationKey;

        this.allLocationId = allLocationId;

        this.cloudConnectServersSupplier = new CloudConnectServersSupplier(this.services.transportService);
        this.companySupplier = new CompanySupplier(this.services.transportService);
        this.locationSupplier = new LocationSupplier(this.services.transportService);

        const portalFilterSectionConfig = this.services.configuratorService.getSectionConfig<PortalFiltersIds>(PortalFiltersIds.PortalFiltersSection);

        this.isSitesFilterVisible = checkItemVisibleInSection(portalFilterSectionConfig, PortalFiltersIds.SitesPortalFilter);
        this.isOrganizationsFilterVisible = checkItemVisibleInSection(portalFilterSectionConfig, PortalFiltersIds.OrganizationsPortalFilter);
        this.isLocationsFilterVisible = checkItemVisibleInSection(portalFilterSectionConfig, PortalFiltersIds.LocationsPortalFilter);

        this.siteFilter = [];
        this.organizationFilter = null;
        this.locationFilter = null;

        this.allLocationsItem = { id: this.allLocationId, name: this.lang.ALL_LOCATIONS };
        this.locations = [this.allLocationsItem];
    }

    @action
    init(): Promise<void> {
        if (!this.noAuth) {
            return this.loadAllData();
        }

        return Promise.resolve();
    }

    @action
    destroy(): void {
        this.setSites([]);
        this.setOrganizations([]);
        this.setOrganizationsMap(null);
        this.setAllLocations([]);
        this.setLocations([this.allLocationsItem]);

        this.resetPortalFilterValues();
    }

    getFilters(): PreparedPortalFilters {
        return {
            ...(this.siteFilter && this.siteFilter.length > 0 ? { siteFilter: this.siteFilter.join(',') } : {}),
            ...(this.organizationFilter ? { organizationFilter: this.organizationFilter } : {}),
            ...(typeof this.locationFilter === 'number' ? { locationFilter: `${this.locationFilter}` } : {}),
        };
    }

    @action.bound
    updatePortalFilters() {
        return this.init();
    }

    @action.bound
    setSiteFilter(sites: string[]): void {
        this.siteFilter = sites;
        this.appStorage.setObjItem(this.siteKey, sites);
    }

    @action.bound
    setOrganizationFilter(organization: CompanyTree | null): void {
        const org = organization && organization.id !== null && organization.id !== undefined
            ? { ...organization, children: [] }
            : null;

        this.organizationFilter = org;
        this.appStorage.setObjItem(this.organizationKey, org);

        this.setLocations(
            org
                ? [this.allLocationsItem].concat(this.allLocations[`${org.id}`] ?? [])
                : [this.allLocationsItem]
        );

        this.setLocationFilter(null);
    }

    @action.bound
    setLocationFilter(locationId: number | null): void {
        const isAllLocation = locationId === this.allLocationId;

        this.locationFilter = isAllLocation || locationId === null ? null : locationId;
        this.appStorage.setItem(this.locationKey, isAllLocation || locationId === null ? null : String(locationId));
    }

    @action
    loadAllData(): Promise<void> {
        return Promise.all([
            this.isSitesFilterVisible ? this.loadSites() : Promise.resolve(),
            this.isOrganizationsFilterVisible ? this.loadCompanies() : Promise.resolve(),
            this.isLocationsFilterVisible ? this.loadAllLocations() : Promise.resolve(),
        ])
            .then(([sitesData, companiesData, locationsData]) => {
                if (sitesData) {
                    this.setSites(sitesData);
                }

                if (companiesData) {
                    this.setOrganizations([companiesData]);
                    this.setOrganizationsMap(flatOrganizationsTree(companiesData));
                }

                if (locationsData) {
                    locationsData.sort((a, b) => b.name < a.name ? 1 : -1);
                    this.setAllLocations(locationsData);
                }
            })
            .then(() => this.selectFilterValues());
    }

    @action
    protected selectFilterValues(): void {
        const siteFromStorage = this.appStorage.getObjItem(this.siteKey) as string[] || [];
        const organizationFromStorage: CompanyTree = this.appStorage.getObjItem(this.organizationKey);
        const locationFromStorage = this.appStorage.getItem(this.locationKey);

        if (this.isSitesFilterVisible && this.sites.length > 0 && siteFromStorage.length > 0) {
            // Sites loaded and exist, values from storage exist, set it even it can reset all
            this.setSiteFilter(siteFromStorage.filter(agentGuid => Boolean(this.sites.find(site => site.agentGuid === agentGuid))));
        }

        if (this.isOrganizationsFilterVisible && this.organizationsMap?.size > 0 && organizationFromStorage) {
            this.setOrganizationFilter(this.organizationsMap.has(organizationFromStorage.id) ? organizationFromStorage : null);
        }

        if (
            !this.isOrganizationsFilterVisible
            && this.isLocationsFilterVisible
            && this.appDataStore.portalUser
            && Object.keys(this.allLocations).length > 0
        ) {
            this.setLocations([this.allLocationsItem].concat(this.allLocations[`${this.appDataStore.portalUser.companyId}`] ?? []));
        }

        if (this.isLocationsFilterVisible && Object.keys(this.allLocations).length > 0 && locationFromStorage) {
            const currentLocation = locationFromStorage
                ? this.locations.find(loc => loc.id === Number(locationFromStorage))
                : null;

            this.setLocationFilter(currentLocation ? currentLocation.id : null);
        }
    }

    @action.bound
    protected resetPortalFilterValues(): void {
        this.siteFilter = [];
        this.organizationFilter = null;
        this.locationFilter = null;
    }

    @action.bound
    protected setSites(sites: CloudConnectServerWithDeployInfo[]): void {
        this.sites = sites;
    }

    @action.bound
    protected setOrganizations(organizations: CompanyTree[]): void {
        this.organizations = organizations;
    }

    @action.bound
    protected setOrganizationsMap(organizationsMap: OrganizationsFlatMap | null): void {
        this.organizationsMap = organizationsMap;
    }

    @action.bound
    protected setAllLocations(locations: BaseLocationData[]): void {
        const allLocations = {};

        locations.forEach((location) => {
            const key = `${location.company}`;

            if (Array.isArray(allLocations[key])) {
                allLocations[key].push(location);
            } else {
                allLocations[key] = [location];
            }
        });

        this.allLocations = allLocations;
    }

    @action.bound
    protected setLocations(locations: BaseLocationData[]): void {
        this.locations = locations.filter(item => Boolean(item));
    }

    protected loadSites(): Promise<CloudConnectServerWithDeployInfo[] | null> {
        return this.cloudConnectServersSupplier.get(this.sitesUrl, { rcopAutoUpdate: true })
            .then(({ data }: RequestSuccessResponse<CloudConnectServerWithDeployInfo[]>) => data)
            .catch((err) => {
                isErrorThatNeedToBeShown(err) && console.error(err);
                return null;
            });
    }

    protected loadCompanies(): Promise<CompanyTree | null> {
        return this.companySupplier.getCompaniesTree(this.organizationsUrl, { rcopAutoUpdate: true })
            .then(({ data }: RequestSuccessResponse<CompanyTree>) => data)
            .catch((err) => {
                isErrorThatNeedToBeShown(err) && console.error(err);
                return null;
            });
    }

    protected loadAllLocations(): Promise<BaseLocationData[] | null> {
        return this.locationSupplier.getLocations(this.locationsUrl, { rcopAutoUpdate: true })
            .then(({ data }: RequestSuccessResponse<BaseLocationData[]>) => data)
            .catch((err) => {
                isErrorThatNeedToBeShown(err) && console.error(err);
                return null;
            });
    }

}
